import { NetworkStatus } from '@apollo/client';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext';
import type { OptionsMap } from '@openx/types/options';
import type { UseTargetablePackagesFetch } from '@openx/types/package/PackageHookTypes';
import { OPEN_AUDIENCE_ORG_ID, OPEN_AUDIENCE_ORG_NAME, OPEN_AUDIENCE_ORG_UID } from '@openx/utils/services/config';
import { useCallback, useMemo } from 'react';
import { useAuth } from '../../../components/AuthContext/context';
import { useOptionsByPathQuery } from '../../../hooks/useOptionsByPathQuery';

export const useTargetablePackagesFetch: UseTargetablePackagesFetch = ({ accountUid, curatedOnly, noCache }) => {
  const { onBehalfOrganization } = useDealContext();
  const { session } = useAuth();

  const isOpenAudienceAccount =
    onBehalfOrganization?.id === OPEN_AUDIENCE_ORG_ID ||
    onBehalfOrganization?.exchange_account_network?.uid === OPEN_AUDIENCE_ORG_UID ||
    (!onBehalfOrganization && session?.account?.name === OPEN_AUDIENCE_ORG_NAME);

  const isSuborgAccount = !isOpenAudienceAccount;

  const fetchPolicySetting = noCache ? { fetchPolicy: 'network-only' as const } : {};

  const shouldFetchOnlyCuratedForSuborg = curatedOnly === true && isSuborgAccount;

  const {
    data: { optionsByPath: targetablePackagesByPath = [] } = {},
    loading: areTargetablePackagesLoading,
    refetch: refetchTargetablePackages,
    networkStatus: targetablePackagesNetworkStatus,
  } = useOptionsByPathQuery({
    skip: !accountUid || shouldFetchOnlyCuratedForSuborg,
    notifyOnNetworkStatusChange: true,
    ...fetchPolicySetting,
    variables: {
      filter: { account_uid: accountUid, is_curated: curatedOnly },
      path: 'deal.package.targetable_package_options',
    },
  });

  const {
    data: { optionsByPath: curatedPackagesByPath = [] } = {},
    loading: areCuratedPackagesLoading,
    refetch: refetchCuratedPackages,
    networkStatus: curatedPackagesNetworkStatus,
  } = useOptionsByPathQuery({
    skip: !accountUid || !shouldFetchOnlyCuratedForSuborg,
    notifyOnNetworkStatusChange: true,
    ...fetchPolicySetting,
    variables: {
      filter: undefined,
      path: 'deal.package.openx_curated_packages',
    },
  });

  const packages = useMemo(() => {
    const packagesByPath = shouldFetchOnlyCuratedForSuborg ? curatedPackagesByPath : targetablePackagesByPath;

    return packagesByPath.reduce<OptionsMap>((acc, pkg) => {
      if (!pkg?.id) {
        return acc;
      }

      acc[pkg.id] = pkg;
      return acc;
    }, {});
  }, [targetablePackagesByPath, curatedPackagesByPath, shouldFetchOnlyCuratedForSuborg]);

  const refetchPackages = useCallback(() => {
    if (shouldFetchOnlyCuratedForSuborg) {
      return refetchCuratedPackages();
    }

    return refetchTargetablePackages();
  }, [refetchTargetablePackages, refetchCuratedPackages, shouldFetchOnlyCuratedForSuborg]);

  return {
    arePackagesFetching: shouldFetchOnlyCuratedForSuborg
      ? curatedPackagesNetworkStatus === NetworkStatus.refetch
      : targetablePackagesNetworkStatus === NetworkStatus.refetch,

    arePackagesLoading: shouldFetchOnlyCuratedForSuborg ? areCuratedPackagesLoading : areTargetablePackagesLoading,

    packages,

    refetchPackages,
  };
};
